import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const EvidenceComparisonTool = ({ dispute }) => {
  const [selectedRenterEvidence, setSelectedRenterEvidence] = useState(dispute?.renterEvidence?.[0] || null);
  const [selectedOwnerEvidence, setSelectedOwnerEvidence] = useState(dispute?.ownerEvidence?.[0] || null);

  const getFileIcon = (type) => {
    switch (type) {
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      case 'document':
        return 'FileText';
      default:
        return 'File';
    }
  };

  const EvidenceViewer = ({ evidence, title, color, onSelect, selected }) => (
    <div className="flex-1">
      <div className={`bg-${color}/10 border border-${color} rounded-lg p-4 mb-4`}>
        <h3 className={`font-body font-semibold text-${color} mb-2 flex items-center space-x-2`}>
          <Icon name="Image" size={18} strokeWidth={2} />
          <span>{title}</span>
        </h3>
        <p className="font-caption text-xs text-muted-foreground">
          {evidence?.length} item{evidence?.length !== 1 ? 's' : ''} submitted
        </p>
      </div>

      {/* Selected Evidence Display */}
      {selected && (
        <div className="bg-card rounded-lg border border-border p-4 mb-4">
          {selected?.type === 'image' ? (
            <img
              src={selected?.url}
              alt={selected?.alt}
              className="w-full h-64 object-contain rounded mb-3"
            />
          ) : (
            <div className="w-full h-64 bg-muted/30 rounded flex items-center justify-center mb-3">
              <Icon
                name={getFileIcon(selected?.type)}
                size={64}
                className="text-primary"
                strokeWidth={1.5}
              />
            </div>
          )}
          <p className="font-body font-semibold text-foreground mb-1">{selected?.name}</p>
          <p className="font-caption text-xs text-muted-foreground mb-2">
            Uploaded: {selected?.timestamp}
          </p>
          {selected?.type !== 'image' && (
            <button className="text-primary font-body text-sm hover:underline flex items-center space-x-1">
              <Icon name="Download" size={16} strokeWidth={2} />
              <span>Download File</span>
            </button>
          )}
        </div>
      )}

      {/* Evidence Thumbnails */}
      <div className="space-y-2">
        <p className="font-body font-semibold text-foreground text-sm">All Evidence</p>
        <div className="grid grid-cols-2 gap-2">
          {evidence?.map((item) => (
            <button
              key={item?.id}
              onClick={() => onSelect(item)}
              className={`rounded-lg border-2 transition-smooth overflow-hidden ${
                selected?.id === item?.id
                  ? 'border-primary' :'border-border hover:border-primary/50'
              }`}
            >
              {item?.type === 'image' ? (
                <img
                  src={item?.url}
                  alt={item?.alt}
                  className="w-full h-20 object-cover"
                />
              ) : (
                <div className="w-full h-20 bg-muted/30 flex items-center justify-center">
                  <Icon
                    name={getFileIcon(item?.type)}
                    size={24}
                    className="text-primary"
                    strokeWidth={2}
                  />
                </div>
              )}
              <div className="p-2 bg-card">
                <p className="font-caption text-xs text-foreground truncate">{item?.name}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Evidence Comparison
        </h2>
        <p className="font-body text-muted-foreground">
          Compare renter's evidence with your counter-evidence side-by-side
        </p>
      </div>

      {/* Comparison View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Renter's Evidence */}
        <EvidenceViewer
          evidence={dispute?.renterEvidence}
          title="Renter's Evidence"
          color="error"
          selected={selectedRenterEvidence}
          onSelect={setSelectedRenterEvidence}
        />

        {/* Owner's Evidence */}
        {dispute?.ownerEvidence?.length > 0 ? (
          <EvidenceViewer
            evidence={dispute?.ownerEvidence}
            title="Your Counter-Evidence"
            color="success"
            selected={selectedOwnerEvidence}
            onSelect={setSelectedOwnerEvidence}
          />
        ) : (
          <div className="flex-1">
            <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4">
              <h3 className="font-body font-semibold text-foreground mb-2 flex items-center space-x-2">
                <Icon name="Image" size={18} strokeWidth={2} />
                <span>Your Counter-Evidence</span>
              </h3>
              <p className="font-caption text-xs text-muted-foreground">
                No counter-evidence uploaded yet
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-body text-muted-foreground mb-4">
                Upload your counter-evidence in the "Respond" tab to support your position
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Comparison Notes */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h4 className="font-body font-semibold text-foreground mb-2 flex items-center space-x-2">
          <Icon name="Info" size={18} strokeWidth={2} />
          <span>Evidence Analysis Tips</span>
        </h4>
        <ul className="space-y-2 font-body text-sm text-muted-foreground">
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Compare timestamps to establish timeline of events</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Look for inconsistencies or contradictions in evidence</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Ensure your counter-evidence directly addresses renter's claims</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Document quality and clarity matter - use high-resolution images</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EvidenceComparisonTool;